"use client";

import { Button } from "@/components/ui/button";
import { m, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { useRef, useCallback, useState, useEffect } from "react";
import { ArrowRight, ChevronDown, Plus } from "lucide-react";
import Magnetic from "@/components/motion/Magnetic";
import type { Hero as HeroData } from "@/lib/validations/cms";
import EditableText from "@/components/edit-mode/EditableText";

import { useIsMobile } from "@/hooks/use-mobile";

const MotionButton = m.create(Button);
const MotionArrow = m.create(ArrowRight);

export default function Hero({ data }: { data: HeroData }) {
  const containerRef = useRef(null);
  const isMobile = useIsMobile();
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isMobile) return;
      if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const { clientX, clientY } = e;
      const xRatio = clientX / window.innerWidth - 0.5;
      const yRatio = clientY / window.innerHeight - 0.5;
      mouseX.set(xRatio);
      mouseY.set(yRatio);
    },
    [mouseX, mouseY, isMobile]
  );

  const moveX = useSpring(useTransform(mouseX, [-0.5, 0.5], [-20, 20]), {
    stiffness: 150,
    damping: 15,
  });
  const moveY = useSpring(useTransform(mouseY, [-0.5, 0.5], [-20, 20]), {
    stiffness: 150,
    damping: 15,
  });

  const squared = !data.disableSquaredPattern;
  const hasLocation = Boolean(data.location.lat.trim() && data.location.lng.trim());

  return (
    <section
      ref={containerRef}
      onMouseMove={isMobile || prefersReduced ? undefined : handleMouseMove}
      className="border-foreground bg-background relative flex min-h-auto flex-col justify-start overflow-hidden border-b lg:min-h-[80vh] lg:justify-center"
    >
      {squared && (
        <m.div
          style={isMobile || prefersReduced ? {} : { y: bgY, x: moveX, translateY: moveY }}
          className="pointer-events-none absolute inset-0 z-0 bg-[linear-gradient(to_right,var(--foreground)_1px,transparent_1px),linear-gradient(to_bottom,var(--foreground)_1px,transparent_1px)] bg-[size:40px_40px] opacity-[0.06]"
        />
      )}

      <div className="text-foreground absolute top-12 left-12 hidden opacity-20 lg:block">
        <Plus className="h-6 w-6" />
      </div>
      <div className="text-foreground absolute right-12 bottom-12 hidden opacity-20 lg:block">
        <Plus className="h-6 w-6" />
      </div>

      <div className="relative z-10 container mx-auto flex flex-col items-center justify-between px-4 pt-16 pb-16 sm:px-6 lg:flex-row lg:px-8 lg:py-0">
        <div className="relative z-20 mb-12 flex w-full flex-col justify-center lg:mb-0 lg:w-7/12">
          <div className="relative">
            {data.label && (
              <m.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-muted-foreground pointer-events-auto absolute -top-10 left-1 flex items-center gap-2 font-mono text-xs tracking-widest uppercase"
              >
                <span className="bg-foreground h-2 w-2 animate-pulse rounded-full"></span>
                <EditableText section="hero" field="label" value={data.label} />
              </m.div>
            )}

            <h1
              className="group text-foreground glitch-text cursor-default leading-[0.75] tracking-tighter uppercase mix-blend-hard-light"
              style={{
                fontFamily: "var(--font-hero-title)",
                fontSize: "var(--hero-title-size)",
                fontWeight: "var(--hero-title-weight)",
              }}
            >
              <m.span
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="neon-glow mb-2 block origin-bottom-left transition-transform duration-300 group-hover:skew-x-12 lg:mb-0"
              >
                <EditableText section="hero" field="titlePart1" value={data.titlePart1} />
              </m.span>
              <m.span
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 1,
                  delay: 0.15,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="stroke-text-thick neon-glow ml-4 block origin-top-right text-transparent transition-transform duration-300 group-hover:-skew-x-12 lg:ml-0"
              >
                <EditableText section="hero" field="titlePart2" value={data.titlePart2} />
              </m.span>
            </h1>
          </div>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="border-foreground mt-8 ml-2 max-w-lg border-l-2 pl-6 lg:mt-12 lg:ml-4"
          >
            <p className="text-muted-foreground mb-8 text-lg leading-relaxed font-medium md:text-xl">
              <EditableText section="hero" field="description" value={data.description} />
            </p>

            <div className="flex flex-col gap-4 sm:flex-row">
              <Magnetic>
                <Link href="/products" passHref className="relative inline-block">
                  <MotionButton
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    initial="initial"
                    className="group border-foreground bg-foreground text-background hover:bg-background hover:text-foreground flex h-14 w-full items-center gap-2 rounded-none border-2 px-10 text-lg font-bold tracking-wider uppercase shadow-[4px_4px_0px_0px_var(--foreground)] transition-all hover:translate-x-[4px] hover:translate-y-[4px] hover:shadow-none sm:w-auto"
                  >
                    <EditableText section="hero" field="buttonText" value={data.buttonText} />
                    <MotionArrow
                      variants={{
                        initial: { x: 0 },
                        hover: { x: 4 },
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                      }}
                      className="h-5 w-5"
                    />
                  </MotionButton>
                </Link>
              </Magnetic>
            </div>
          </m.div>
        </div>

        <m.div
          style={!isMobile && !prefersReduced ? { y, opacity } : {}}
          className="relative z-0 h-[50vh] w-full pl-0 lg:z-10 lg:h-[70vh] lg:w-5/12 lg:pl-10"
        >
          <div className="border-foreground bg-muted relative h-full w-full border-2 p-2">
            <div className="border-foreground absolute -top-1 -left-1 z-30 h-4 w-4 border-t-2 border-l-2"></div>
            <div className="border-foreground absolute -top-1 -right-1 z-30 h-4 w-4 border-t-2 border-r-2"></div>
            <div className="border-foreground absolute -bottom-1 -left-1 z-30 h-4 w-4 border-b-2 border-l-2"></div>
            <div className="border-foreground absolute -right-1 -bottom-1 z-30 h-4 w-4 border-r-2 border-b-2"></div>

            <div className="bg-background relative h-full w-full overflow-hidden">
              <m.div
                initial={{ scale: 1.2, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0"
              >
                <Image
                  src={data.image}
                  alt="Model"
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 40vw"
                  className="object-cover transition-all duration-700 ease-in-out hover:scale-105 hover:grayscale-[50%]"
                  priority
                  fetchPriority="high"
                />
              </m.div>

              {hasLocation && (
                <div className="bg-foreground/50 text-background pointer-events-auto absolute right-4 bottom-4 z-20 px-2 py-1 text-right font-mono text-xs mix-blend-difference backdrop-blur-sm">
                  <EditableText
                    section="hero"
                    field="location.lat"
                    value={data.location.lat}
                    as="p"
                  />
                  <EditableText
                    section="hero"
                    field="location.lng"
                    value={data.location.lng}
                    as="p"
                  />
                </div>
              )}
            </div>
          </div>
        </m.div>
      </div>

      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="pointer-events-auto absolute bottom-8 left-1/2 z-20 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
      >
        <span className="text-muted-foreground font-mono text-[10px] tracking-widest uppercase">
          <EditableText section="hero" field="scrollText" value={data.scrollText} />
        </span>
        <m.div
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        </m.div>
      </m.div>

      <div className="bg-foreground absolute bottom-0 left-0 h-px w-full"></div>
    </section>
  );
}
